import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, GitCompare } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { fmtDateTime, fmtMoney } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { EmptyState, PageHeader, ToneChip } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

function ComparisonDetail({ id }) {
  const { map } = useUsers();
  const [dialog, setDialog] = useState(null); // entry seleccionada no recomendada
  const [justification, setJustification] = useState("");
  const { data, refetch } = useQuery({
    queryKey: ["fin-comparison", id],
    queryFn: async () => (await api.get(`/finanzas/comparisons/${id}`)).data,
  });
  if (!data) return null;
  const { comparison: comp, purchase_request: pr } = data;

  const select = async (quoteId, just = "") => {
    try {
      await api.post(`/finanzas/comparisons/${comp.id}/select`, { quote_id: quoteId, justification: just });
      toast.success("Oferta seleccionada — decisión humana registrada");
      setDialog(null);
      setJustification("");
      refetch();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-4" data-testid={`comparison-${comp.id}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <ToneChip label={comp.comparison_number} tone="info" />
        {comp.selected_quote_id
          ? <span className="ts-chip ts-chip-success" data-testid="comparison-selected-chip">Seleccionada por {userName(map, comp.selected_by)}</span>
          : <span className="ts-chip ts-chip-warning" data-testid="comparison-pending-chip">Pendiente de selección humana</span>}
        {pr && <span className="text-xs text-slate-500">Solicitud: {pr.pr_number} — {pr.description}</span>}
      </div>
      <div className="rounded-sm border border-violet-200 bg-violet-50 p-3 text-sm text-violet-900" data-testid="comparison-recommendation">
        <strong>Recomendación IA (no vinculante):</strong> {comp.recommendation?.supplier} — {comp.recommendation?.justification}
      </div>
      <div className="ts-card mt-3 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Oferta</TableHead><TableHead>Proveedor</TableHead><TableHead>Plazo</TableHead>
              <TableHead>Condiciones</TableHead><TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Puntuación</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {comp.entries.map((e) => (
              <TableRow key={e.quote_id} data-testid={`comparison-entry-${e.quote_id}`} className={comp.selected_quote_id === e.quote_id ? "bg-emerald-50/50" : ""}>
                <TableCell className="font-mono text-xs">{e.quote_number}</TableCell>
                <TableCell className="text-sm font-medium">
                  {e.supplier_name}
                  {comp.recommendation?.quote_id === e.quote_id && <span className="ts-chip ts-chip-ai ml-2">Recomendada</span>}
                </TableCell>
                <TableCell className="text-xs">{e.delivery_weeks ? `${e.delivery_weeks} sem.` : "—"}</TableCell>
                <TableCell className="text-xs">{e.payment_terms}</TableCell>
                <TableCell className="text-right font-mono text-sm">{fmtMoney(e.total)}</TableCell>
                <TableCell className="text-right font-mono text-sm font-semibold">{e.score}</TableCell>
                <TableCell className="text-right">
                  {!comp.selected_quote_id && (
                    comp.recommendation?.quote_id === e.quote_id
                      ? <Button size="sm" onClick={() => select(e.quote_id)} data-testid={`select-quote-${e.quote_id}`}><Check className="mr-1 h-3.5 w-3.5" /> Seleccionar</Button>
                      : <Button size="sm" variant="outline" onClick={() => setDialog(e)} data-testid={`select-quote-${e.quote_id}`}>Seleccionar (justificar)</Button>
                  )}
                  {comp.selected_quote_id === e.quote_id && <Check className="ml-auto h-4 w-4 text-emerald-600" />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="mt-2 text-xs text-slate-400">Criterios: {comp.criteria.join(" · ")} — {fmtDateTime(comp.created_at)}</p>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent data-testid="select-justification-dialog">
          <DialogHeader><DialogTitle>Seleccionar {dialog?.supplier_name} (distinta de la recomendada)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Elegir una oferta distinta de la recomendación IA exige una justificación que quedará registrada.</p>
            <div><Label>Justificación *</Label><Textarea value={justification} onChange={(e) => setJustification(e.target.value)} data-testid="select-justification-input" /></div>
            <Button className="w-full" disabled={!justification.trim()} onClick={() => select(dialog.quote_id, justification)} data-testid="select-justification-confirm">Confirmar selección</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Comparaciones() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: items } = useQuery({
    queryKey: ["fin-comparisons"],
    queryFn: async () => (await api.get("/finanzas/comparisons")).data,
  });

  if (id) {
    return (
      <div data-testid="comparaciones-detail-page">
        <PageHeader title="Comparación de ofertas" subtitle="Matriz ponderada demo. La IA recomienda; la decisión es siempre humana y justificada." />
        <ComparisonDetail id={id} />
        <Button variant="ghost" size="sm" className="mt-4" onClick={() => navigate("/finanzas/comparaciones")} data-testid="back-to-comparisons">← Volver a comparaciones</Button>
      </div>
    );
  }

  return (
    <div data-testid="comparaciones-page">
      <PageHeader title="Comparaciones de ofertas" subtitle="Cuadros comparativos con recomendación IA pendiente de validación humana." />
      {items && items.length === 0 && <EmptyState title="Sin comparaciones" description="Se crean desde una solicitud de compra con al menos dos ofertas validadas." />}
      <div className="grid gap-4 md:grid-cols-2">
        {(items || []).map((c) => (
          <div key={c.id} className="ts-card ts-card-hover cursor-pointer p-5" data-testid={`comparison-card-${c.id}`} onClick={() => navigate(`/finanzas/comparaciones/${c.id}`)}>
            <div className="flex items-center gap-2">
              <GitCompare className="h-4 w-4 text-slate-400" />
              <p className="font-display text-base font-semibold">{c.comparison_number}</p>
              {c.selected_quote_id ? <span className="ts-chip ts-chip-success">Seleccionada</span> : <span className="ts-chip ts-chip-warning">Pendiente</span>}
            </div>
            <p className="mt-2 text-sm text-slate-600">{c.entries.length} ofertas · Recomendación IA: {c.recommendation?.supplier}</p>
            <p className="mt-1 text-xs text-slate-400">{fmtDateTime(c.created_at)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
